/**
 * KML Bounds Facade Extension
 *
 * Extends the map facade with support for managing a market area KML boundary layer
 * and fitting the map viewport to coordinate bounds.
 *
 * Single responsibility: infrastructure-only KML and bounds operations.
 * No business logic. All orchestration decisions live in the plugin layer.
 *
 * Architecture:
 * - Follows the same mixin pattern as overlays.js, svgOverlays.js, and markers.js.
 * - Receives (facade, state) where state is the adapter's internal state.
 * - Returns an extended facade object with KML-specific methods added.
 * - Manages one market boundary KML layer at a time (separate from the location boundary).
 *
 * KML settle contract:
 * - `createKmlLayer(url)` returns `{ onKmlSettled }` — a Promise that always resolves
 *   after the KML layer reports its final status via `status_changed`.
 * - `defaultviewport_changed` is captured first (when it fires before `status_changed`
 *   on a successful load) to record the actual KML viewport bounds.
 * - `status_changed` is the definitive signal — the promise always resolves on this event,
 *   preventing the caller from ever waiting indefinitely.
 *
 * @module mapLibrary/kmlBounds
 */

import fitBoundsOnMap from './boundsUtils';

/**
 * Extends the map facade with KML boundary layer management and bounds fitting.
 *
 * @param {Object} facade - The base facade object to extend.
 * @param {Object} state  - The adapter's internal state ({ google, map }).
 * @returns {Object} Extended facade.
 */
export default function kmlBounds(facade, state) {
  let marketBoundaryLayer = null;

  function getGoogle() {
    return state.google;
  }

  function getMap() {
    return state.map;
  }

  /**
   * Removes the market area KML layer from the map and clears the internal reference.
   * Safe to call when no layer is currently active.
   */
  function removeKmlLayer() {
    if (marketBoundaryLayer) {
      marketBoundaryLayer.setMap(null);
      marketBoundaryLayer = null;
    }
  }

  /**
   * Creates a KML layer for the market area boundary.
   * Any previously existing market boundary layer is removed before creating the new one.
   *
   * Returns `{ onKmlSettled }` — a Promise that resolves with either:
   * - `{ success: true, kmlBounds: { minLatitude, minLongitude, maxLatitude, maxLongitude } }`
   *   when the KML renders successfully and exposes viewport bounds.
   * - `{ success: false, kmlBounds: null }` when the KML fails to load.
   *
   * The promise always resolves (never rejects) to keep error handling simple in the plugin.
   *
   * @param {string} url - The KML file URL.
   * @returns {{ onKmlSettled: Promise<{ success: boolean, kmlBounds: Object|null }> }}
   */
  function createKmlLayer(url) {
    removeKmlLayer();

    const google = getGoogle();
    const map = getMap();

    const layer = new google.maps.KmlLayer(url, {
      suppressInfoWindows: true,
      preserveViewport: true,
      map,
    });

    marketBoundaryLayer = layer;

    let kmlBoundsFromViewport = null;

    // `defaultviewport_changed` fires before `status_changed` on a successful load.
    // Capture the actual KML bounds here so they are ready when status_changed fires.
    google.maps.event.addListenerOnce(layer, 'defaultviewport_changed', () => {
      const viewport = layer.getDefaultViewport();
      const southWest = viewport.getSouthWest();
      const northEast = viewport.getNorthEast();
      kmlBoundsFromViewport = {
        minLatitude: southWest.lat(),
        minLongitude: southWest.lng(),
        maxLatitude: northEast.lat(),
        maxLongitude: northEast.lng(),
      };
    });

    // `status_changed` is always fired after the KML layer finishes loading (success or failure).
    // Using it as the definitive settle point ensures the promise always resolves.
    const onKmlSettled = new Promise((resolve) => {
      google.maps.event.addListenerOnce(layer, 'status_changed', () => {
        if (layer.getStatus() === 'OK') {
          resolve({ success: true, kmlBounds: kmlBoundsFromViewport });
        } else {
          resolve({ success: false, kmlBounds: null });
        }
      });
    });

    return { onKmlSettled };
  }

  /**
   * Fits the map viewport to the provided coordinate bounds.
   * Uses the frontend naming convention: minLatitude, minLongitude, maxLatitude, maxLongitude.
   *
   * @param {{ minLatitude: number, minLongitude: number, maxLatitude: number, maxLongitude: number }} bounds
   */
  function fitBounds(bounds) {
    fitBoundsOnMap(getGoogle(), getMap(), bounds);
  }

  return {
    ...facade,
    createKmlLayer,
    removeKmlLayer,
    fitBounds,
  };
}
